import { useCallback, useEffect, useMemo, useState } from "react";

import { AnalyticsResourceType } from "@doitintl/cmp-models";
import { Box, Button, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid2";
import eq from "lodash/eq";

import { globalText, reportText } from "../../../assets/texts";
import GkePartialCostAllocationAlert from "../../../Components/Alerts/GkePartialCostAllocationAlert";
import DeleteDialog from "../../../Components/DeleteDialog";
import { DoitConsoleTitle } from "../../../Components/DoitConsoleTitle";
import { FilterTable } from "../../../Components/FilterTable/FilterTable";
import { FilterTableSkeleton } from "../../../Components/FilterTable/FilterTableSkeleton";
import Hide from "../../../Components/HideChildren/Hide";
import { useCloudAnalyticsStatus } from "../../../Components/hooks/cloudAnalytics/reports/useCloudAnalyticsStatus";
import useRouteMatchURL from "../../../Components/hooks/useRouteMatchURL";
import { cloudAnalytics } from "../../../constants/cypressIds";
import { useAuthContext } from "../../../Context/AuthContext";
import { useIsFeatureEntitled } from "../../../Context/TierProvider";
import { type ReportWSnap } from "../../../types";
import mixpanel from "../../../utils/mixpanel";
import { addAnalyticsWindowFunction } from "../../../utils/windowInit";
import { reportsToAnalyticsResources } from "../analyticsResources/utils";
import ShareDialog from "../dialogs/shareDialog/ShareDialog";
import { useSelectionOperations } from "../hooks/useSelectionOperations";
import { EditLabelsMenuButton } from "../labels/components/EditLabelsMenuButton";
import { useLabels } from "../labels/hooks";
import { updateColumnFiltersWithLabels } from "../labels/labelTableUtils";
import { useNewReportHandler } from "../report/hooks";
import ReportStatus from "../statuses/ReportStatus";
import { CloudAnalyticsEntities } from "../utilities";
import { filters, headerColumns } from "./Columns";
import { useDeleteReportsHandler, useShareReportsHandler } from "./hooks";
import { ReportRow } from "./ReportRow";

const { report: reportIds } = cloudAnalytics;

type ReportBrowserProps = {
  reports: ReportWSnap[];
  loading: boolean;
};

export const ReportBrowser = ({ reports, loading }: ReportBrowserProps) => {
  const { isDoitEmployee } = useAuthContext({ mustHaveUser: true });
  const routeMatchURL = useRouteMatchURL();
  const isEntitledAnalyticsReports = useIsFeatureEntitled("analytics:reports");
  const [selected, setSelected] = useState<ReportWSnap[]>([]);
  // State for share dialog
  const [clickedRow, setClickedRow] = useState<ReportWSnap>({} as ReportWSnap);
  // Dialog states
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [bulkShareDialogOpen, setBulkShareDialogOpen] = useState(false);
  const [shareDialogLoading, setShareDialogLoading] = useState(false);
  const [deleteDialog, setOpenDeleteDialog] = useState(false);
  const [labels, labelsLoading] = useLabels();

  const [status] = useCloudAnalyticsStatus();

  const {
    canDelete,
    canEdit: canEditSelected,
    selectedWithoutPresets,
    presetSelected,
  } = useSelectionOperations(selected);

  useEffect(() => {
    const updatedSelectedReports = reports.filter((report) => selected.some((x) => x.ref.id === report.ref.id));

    setSelected(updatedSelectedReports);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);

  const handleChangeSharing = useShareReportsHandler({
    selected: selectedWithoutPresets.length ? selectedWithoutPresets : [clickedRow],
    setShareDialogLoading,
    closeDialog: () => {
      setBulkShareDialogOpen(false);
      setShareDialogLoading(false);
      setShareDialogOpen(false);
    },
  });

  const handleNewReport = useNewReportHandler({ baseUrl: routeMatchURL, mixpanelEventName: "analytics.reports.new" });

  useEffect(() => {
    addAnalyticsWindowFunction({ handleNewReport });
  }, [handleNewReport]);

  const handleDeleteReport = useDeleteReportsHandler(selectedWithoutPresets, () => {
    setSelected([]);
  });

  useEffect(() => {
    mixpanel.track("analytics.reports.list");
  }, []);

  const ReportRowWrapper = useCallback(
    ({ row }: { row: ReportWSnap }) => (
      <ReportRow
        setOpenDeleteDialog={setOpenDeleteDialog}
        row={row}
        labels={labels}
        setClickedRow={setClickedRow}
        setShareDialogOpen={setShareDialogOpen}
        setSelectedReports={setSelected}
      />
    ),
    [labels, setOpenDeleteDialog, setClickedRow, setShareDialogOpen, setSelected]
  );

  const selectedReportsHaveSameOrganization = useMemo<boolean>(
    () =>
      selectedWithoutPresets.length === 0 ||
      selectedWithoutPresets.every((report) =>
        eq(report.data.organization, selectedWithoutPresets[0].data.organization)
      ),
    [selectedWithoutPresets]
  );

  const canEdit = canEditSelected && selectedReportsHaveSameOrganization;

  const deleteTooltip = useMemo(() => {
    if (canDelete || selected.length === 0) {
      return "";
    }
    if (presetSelected) {
      return reportText.REPORT_PRESET_NOT_DEL;
    }
    return reportText.REPORT_NOT_OWNER;
  }, [canDelete, presetSelected, selected.length]);

  const editPermissionsTooltip = useMemo(() => {
    if (selected.length === 0) {
      return "";
    }

    if (!selectedReportsHaveSameOrganization) {
      return reportText.REPORT_NOT_SAME_ORG;
    }

    if (!canEdit) {
      return globalText.NO_PERMISSION_UPDATE_RESOURCE;
    }
    return "";
  }, [selected, selectedReportsHaveSameOrganization, canEdit]);

  const shareDialogOrganization = useMemo(() => {
    if (selected.length > 0 && bulkShareDialogOpen) {
      return selected[0].data.type === AnalyticsResourceType.CUSTOM ? selected[0].data?.organization : null;
    }

    return clickedRow.data?.type === AnalyticsResourceType.CUSTOM ? clickedRow.data?.organization : null;
  }, [bulkShareDialogOpen, clickedRow.data?.organization, clickedRow.data?.type, selected]);

  const loadingPage = useMemo(
    () => (
      <>
        <DoitConsoleTitle pageName="Reports" />
        <Box
          sx={{
            p: 1,
          }}
        >
          <FilterTableSkeleton />
        </Box>
      </>
    ),
    []
  );

  const columnFiltersWithLabels = useMemo(() => updateColumnFiltersWithLabels(labels, filters, 4), [labels]);

  if (labelsLoading) {
    return loadingPage;
  }

  if (!labels) {
    return null;
  }

  return (
    <>
      {loading ? (
        loadingPage
      ) : (
        <>
          <DoitConsoleTitle pageName="Reports" />
          <GkePartialCostAllocationAlert />
          <FilterTable<ReportWSnap>
            showRowsSelection={true}
            onRowsSelected={setSelected}
            tableItems={reports}
            rowComponent={ReportRowWrapper}
            headerColumns={headerColumns}
            filterColumns={columnFiltersWithLabels}
            filterBarPlaceholder={reportText.FILTER_REPORTS}
            persistenceKey="cloud_analytics_reports_v1"
            itemUniqIdentifierField="ref.id"
            defaultSortingColumnValue="data.timeModified"
            toolbarProps={{
              title: "Reports",
              allowToEditColumns: true,
              deleteButton: {
                text: globalText.DELETE,
                onClick: () => {
                  setOpenDeleteDialog(true);
                },
                disabled: !canDelete,
                tooltipTitle: deleteTooltip,
                "data-cy": reportIds.browser.delete,
              },
              customSlot: (
                <>
                  <Grid data-cy={reportIds.browser.explore}>
                    <Hide mdDown>
                      <Button
                        color="primary"
                        variant="contained"
                        onClick={handleNewReport}
                        data-testid="new-report-button"
                        disabled={!isDoitEmployee && !isEntitledAnalyticsReports}
                      >
                        {reportText.CREATE_NEW_REPORT}
                      </Button>
                    </Hide>
                  </Grid>
                  <Grid>
                    <EditLabelsMenuButton
                      labels={labels}
                      selectedResources={reportsToAnalyticsResources(selected)}
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid>
                    <Tooltip arrow title={editPermissionsTooltip}>
                      <span>
                        <Button
                          variant="text"
                          onClick={() => {
                            setBulkShareDialogOpen(true);
                          }}
                          disabled={!canEdit}
                        >
                          {globalText.EDIT_PERMISSIONS}
                        </Button>
                      </span>
                    </Tooltip>
                  </Grid>
                </>
              ),
            }}
          />
          <ReportStatus key="status-dialog" statusSnap={status} />
        </>
      )}
      {deleteDialog && (
        <DeleteDialog
          open={deleteDialog}
          title={reportText.DELETE_SELECTED}
          message={reportText.DELETE_MESSAGE}
          onDelete={handleDeleteReport}
          onClose={() => {
            setOpenDeleteDialog(false);
          }}
        />
      )}
      {(shareDialogOpen || bulkShareDialogOpen) && (
        <ShareDialog
          open={shareDialogOpen || bulkShareDialogOpen}
          onClose={() => {
            setShareDialogOpen(false);
            setBulkShareDialogOpen(false);
          }}
          entity={CloudAnalyticsEntities.REPORT}
          onSharingChanged={handleChangeSharing}
          loading={shareDialogLoading}
          organization={shareDialogOrganization}
          shareEntities={bulkShareDialogOpen ? selectedWithoutPresets.map((x) => x.data) : [clickedRow.data]}
          showPresetExcludedAlert={presetSelected}
        />
      )}
    </>
  );
};
